import { useState } from 'react';
import { useInstructorAttempts, useGradeAnswer } from '@/api/instructor';
import { LoadingSpinner } from '@/components/shared/loading-spinner';
import { ErrorMessage } from '@/components/shared/error-message';
import { EmptyState } from '@/components/shared/empty-state';
import { Pagination } from '@/components/shared/pagination';
import { cn } from '@/lib/utils';
import { formatDateTime } from '@/lib/formatters';
import { CheckCircle, XCircle, Clock, ClipboardCheck } from 'lucide-react';
import type { InstructorAttempt } from '@/types';

const STATUS_BADGES: Record<string, string> = {
  submitted: 'bg-amber-100 text-amber-700',
  graded: 'bg-green-100 text-green-700',
};

const STATUS_LABELS: Record<string, string> = {
  submitted: 'Pendiente',
  graded: 'Calificado',
};

export function QuizGradingPage() {
  const [tab, setTab] = useState<'submitted' | 'graded'>('submitted');
  const [page, setPage] = useState(1);
  const { data, isLoading, error } = useInstructorAttempts({
    status: tab,
    page,
    per_page: 20,
  });

  const attempts = data?.data ?? [];
  const pagination = data?.meta?.pagination;

  if (isLoading) return <LoadingSpinner />;
  if (error) return <ErrorMessage message="No se pudieron cargar los intentos de quiz." />;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-foreground">Calificar Quizzes</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Revisa y califica los intentos de quiz de tus cursos.
        </p>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 rounded-lg bg-muted p-1">
        {(['submitted', 'graded'] as const).map((t) => (
          <button
            key={t}
            onClick={() => {
              setTab(t);
              setPage(1);
            }}
            className={cn(
              'flex-1 rounded-md px-3 py-2 text-sm font-medium transition-colors',
              tab === t
                ? 'bg-background text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground',
            )}
          >
            {t === 'submitted' ? 'Pendientes' : 'Calificados'}
          </button>
        ))}
      </div>

      {!attempts.length ? (
        <EmptyState
          title={tab === 'submitted' ? 'Sin quizzes pendientes' : 'Sin quizzes calificados'}
          description={
            tab === 'submitted'
              ? 'No hay intentos de quiz pendientes de calificación.'
              : 'No hay intentos calificados aún.'
          }
        />
      ) : (
        <>
          <div className="grid gap-4 sm:grid-cols-2">
            {attempts.map((attempt) => (
              <AttemptCard key={attempt.id} attempt={attempt} showGrading={tab === 'submitted'} />
            ))}
          </div>

          {pagination && <Pagination meta={pagination} onPageChange={setPage} />}
        </>
      )}
    </div>
  );
}

function AttemptCard({
  attempt,
  showGrading,
}: {
  attempt: InstructorAttempt;
  showGrading: boolean;
}) {
  const [expanded, setExpanded] = useState(false);
  const gradeAnswer = useGradeAnswer();

  const studentName = `${attempt.user.first_name} ${attempt.user.last_name}`;
  const courseName = attempt.quiz.lesson.section.course.title;
  const writtenAnswers = attempt.answers.filter((a) => a.written_answer !== null);
  const ungradedCount = writtenAnswers.filter((a) => a.is_correct === null).length;

  const handleGrade = (answerId: number, isCorrect: boolean, points: number) => {
    gradeAnswer.mutate({ answerId, data: { is_correct: isCorrect, points_awarded: points } });
  };

  return (
    <div className="rounded-xl border border-border bg-card p-5">
      <div className="flex items-start gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/10">
          <ClipboardCheck className="h-5 w-5 text-primary" />
        </div>
        <div className="min-w-0 flex-1">
          <h3 className="text-sm font-semibold text-card-foreground">{studentName}</h3>
          <p className="text-xs text-muted-foreground">{attempt.quiz.title}</p>
          <p className="text-xs text-muted-foreground">{courseName}</p>

          <div className="mt-2 flex flex-wrap gap-1.5">
            <span
              className={cn(
                'rounded-full px-2 py-0.5 text-xs font-medium',
                STATUS_BADGES[attempt.status],
              )}
            >
              {STATUS_LABELS[attempt.status]}
            </span>
            {attempt.score != null && (
              <span className="flex items-center gap-1 text-xs font-medium text-muted-foreground">
                {attempt.score}%
                {attempt.passed ? (
                  <CheckCircle className="h-3 w-3 text-green-600" />
                ) : (
                  <XCircle className="h-3 w-3 text-red-600" />
                )}
              </span>
            )}
            {ungradedCount > 0 && (
              <span className="flex items-center gap-1 rounded-full bg-orange-100 px-2 py-0.5 text-xs font-medium text-orange-700">
                <Clock className="h-3 w-3" /> {ungradedCount} sin calificar
              </span>
            )}
          </div>

          <div className="mt-2 text-xs text-muted-foreground">
            {attempt.submitted_at && <p>Enviado: {formatDateTime(attempt.submitted_at)}</p>}
          </div>

          {showGrading && writtenAnswers.length > 0 && (
            <div className="mt-3">
              <button
                onClick={() => setExpanded(!expanded)}
                className="text-xs font-medium text-primary hover:underline"
              >
                {expanded
                  ? 'Ocultar respuestas'
                  : `Ver ${writtenAnswers.length} respuesta(s) escrita(s)`}
              </button>

              {expanded && (
                <div className="mt-2 space-y-3">
                  {writtenAnswers.map((answer) => (
                    <div
                      key={answer.id}
                      className="rounded-lg border border-border bg-muted/50 p-3"
                    >
                      <p className="text-xs text-muted-foreground">
                        Pregunta #{answer.question_id}
                      </p>
                      <p className="mt-1 text-sm text-card-foreground">{answer.written_answer}</p>
                      {answer.is_correct === null ? (
                        <div className="mt-2 flex gap-2">
                          <button
                            onClick={() => handleGrade(answer.id, true, 1)}
                            disabled={gradeAnswer.isPending}
                            className="rounded-md bg-green-600 px-3 py-1 text-xs font-medium text-white hover:bg-green-700 disabled:opacity-50"
                          >
                            Correcta
                          </button>
                          <button
                            onClick={() => handleGrade(answer.id, false, 0)}
                            disabled={gradeAnswer.isPending}
                            className="rounded-md bg-red-600 px-3 py-1 text-xs font-medium text-white hover:bg-red-700 disabled:opacity-50"
                          >
                            Incorrecta
                          </button>
                        </div>
                      ) : (
                        <p className="mt-1 text-xs font-medium">
                          {answer.is_correct ? (
                            <span className="text-green-600">
                              Correcta ({answer.points_awarded} pts)
                            </span>
                          ) : (
                            <span className="text-red-600">Incorrecta (0 pts)</span>
                          )}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
